import React from 'react';

import { assertNeverSafely } from '@aline/common';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { Alert, Dialog, DialogActions, Typography } from '@mui/material';
import type { FileStorageConnection } from 'src/components/admin/integrations/IntegrationRow';
import { useFileStorageSession } from 'src/components/admin/integrations/IntegrationRow';
import type { AlineButtonProps } from 'src/components/generic';
import { AlineButton, SmallCloseIconButton } from 'src/components/generic';
import { Apollo } from 'src/gql/generated';
import { useBulkCreateFolderSyncJobs } from 'src/hooks/folder-sync';
import { makeStyles } from 'tss-react/mui';

import { FileTypeSelector } from './FileTypeSelector';
import { DROPBOX_FILE_TYPE_FILTER_WARNING } from './FolderSyncJobsList';
import { IntegrationDriveTreeView } from './IntegrationDriveTreeView';
import { toDriveSyncs } from './useFolderSyncJobs';
import type { TreeNode } from './useFolderSyncJobs';

const useStyles = makeStyles()(theme => ({
  closeButton: {
    alignSelf: 'flex-start',
  },
  dialogContent: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(4),
    height: '100%',
    overflow: 'auto',
    padding: theme.spacing(4, 0),
  },
  footer: {
    padding: theme.spacing(0, 4),
  },
  header: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 4, 4, 4),
  },
  mainContainer: {
    borderRadius: 8,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(1.5),
    minHeight: 0,
    overflow: 'auto',
    padding: theme.spacing(0, 4),
  },
  paper: {
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: theme.dialogStyles.mediumDialogWidth,
    width: theme.dialogStyles.mediumDialogWidth,
  },
}));

export type SelectionState = {
  allowedMimeTypes: string[];
  selectedIds: Set<string>;
  treeMap: Map<string, TreeNode>;
};

export const selectionStateVar = makeVar<SelectionState>({
  allowedMimeTypes: [],
  selectedIds: new Set(),
  treeMap: new Map(),
});

export function setSelectionState(state: Partial<SelectionState>) {
  selectionStateVar({
    ...selectionStateVar(),
    ...state,
  });
}

export function FileStorageSyncDialog({
  buttonProps,
  buttonText,
  connections,
  folderSyncFolderIds,
  integration,
}: {
  buttonProps: AlineButtonProps;
  buttonText: string;
  connections: FileStorageConnection[];
  folderSyncFolderIds: string[];
  integration: Apollo.FileStorageIntegration;
}): JSX.Element {
  const { classes } = useStyles();
  const name = getNameFromServiceId(integration);
  const { dialogState, openVaultOrFolderPicker, setDialogState } =
    useFileStorageSession(connections);
  const { allowedMimeTypes, selectedIds, treeMap } = useReactiveVar(selectionStateVar);
  const { bulkCreateFolderSyncJobs, loading: creatingSyncJobs } =
    useBulkCreateFolderSyncJobs(integration);
  function onClose() {
    setDialogState({
      open: false,
      serviceId: null,
    });
  }

  function handleMimeTypesChange(mimeTypes: string[]) {
    setSelectionState({ allowedMimeTypes: mimeTypes });
  }

  return (
    <>
      <AlineButton {...buttonProps} onClick={() => openVaultOrFolderPicker(integration)}>
        {buttonText}
      </AlineButton>
      <Dialog
        classes={{ paper: classes.paper }}
        open={dialogState.open}
        slotProps={{
          transition: {
            onEnter: () => {
              setSelectionState({
                allowedMimeTypes: [],
                selectedIds: new Set(folderSyncFolderIds),
              });
            },
            onExit: () => {
              setSelectionState({
                allowedMimeTypes: [],
                selectedIds: new Set(folderSyncFolderIds),
              });
            },
          },
        }}
        onClose={onClose}
      >
        <div className={classes.dialogContent}>
          <div className={classes.header}>
            <Typography variant="h1">Sync {name}</Typography>
            <SmallCloseIconButton className={classes.closeButton} onClick={onClose} />
          </div>
          <div className={classes.mainContainer}>
            <Typography variant="subtitle2">
              Select the file types you want to sync (leave empty to sync all types):
            </Typography>
            {integration === Apollo.FileStorageIntegration.Dropbox && (
              <Alert severity="warning">{DROPBOX_FILE_TYPE_FILTER_WARNING}</Alert>
            )}
            <FileTypeSelector value={allowedMimeTypes} onChange={handleMimeTypesChange} />
            <Typography variant="subtitle2">Select the drives/folders you want to sync:</Typography>
            <IntegrationDriveTreeView
              folderSyncFolderIds={folderSyncFolderIds}
              integration={integration}
            />
          </div>
          <DialogActions className={classes.footer}>
            <AlineButton color="primary" size="small" variant="outlined" onClick={onClose}>
              Cancel
            </AlineButton>
            <AlineButton
              color="primary"
              disabled={selectedIds.size === 0}
              loading={creatingSyncJobs}
              size="small"
              variant="contained"
              onClick={async () => {
                await bulkCreateFolderSyncJobs({
                  variables: {
                    input: {
                      driveSyncs: toDriveSyncs({
                        allowedMimeTypes,
                        folderSyncFolderIds,
                        selectedIds,
                        treeMap,
                      }),
                      integration,
                    },
                  },
                });
                onClose();
              }}
            >
              Sync
            </AlineButton>
          </DialogActions>
        </div>
      </Dialog>
    </>
  );
}

function getNameFromServiceId(serviceId: Apollo.FileStorageIntegration): string {
  switch (serviceId) {
    case Apollo.FileStorageIntegration.GoogleDrive:
      return 'Google Drive';
    case Apollo.FileStorageIntegration.OneDrive:
      return 'OneDrive';
    case Apollo.FileStorageIntegration.Dropbox:
      return 'Dropbox';
    case Apollo.FileStorageIntegration.Box:
      return 'Box';
    case Apollo.FileStorageIntegration.SharePoint:
      return 'SharePoint';
    default:
      assertNeverSafely(serviceId);
      return 'Unknown';
  }
}
